//#region Util Imports

import * as _ from 'underscore'
import * as $ from 'jquery'

//#endregion

//#region DevExpress Imports

import DevExpress from 'devextreme'
import dxDataGrid from 'devextreme/ui/data_grid'
import { Options as dxToolbarOptions } from 'devextreme/ui/toolbar'

//#endregion

import { ColumnModel } from "../../modules/api/api.models"

import { Enums } from "../../models/common"
import { OpDataTypeInGroup, OpView, OpViewColumn } from "../../models/core"
import { IOpObject, IReferenceType } from '../../models/util/interfaces'

import { ReferenceTypeComponent, DataTypeGroupComponent, TypeHelper, CommonHelper, LocalizationHelper, LogHelper } from "../../helpers"
import { USER_SESSION_SERVICE_NAME, UserSessionService } from '../user-session'


// #region Consts

export const GRID_SERVICE_NAME = 'GridService'
export const GRID_SERVICE_PROVIDER_NAME = 'GridServiceProvider'

// #endregion

// #region Public Types

// #endregion

// #region Private Types

type GetColumnModelOptions = {

    /**
     * Reference type for which the groups should be search for.
     */
    groupReferenceType?: Enums.ReferenceType,

    /**
     * Reference value for which the groups should be search for.
     */
    groupReferenceValue?: number,

    /**
     * Reference type of a view that the columns should be paired with.
     */
    viewReferenceType?: Enums.ReferenceType,

    /**
     * A prefix for the column's dataField name, i.e. {prefix}_10005_0.
     */
    prefix?: string

    /**  
    * If view is not defined get columns.
    */
    skipView?: boolean
}

// #endregion

export class GridService {
    // #region Consts

    public static get CELL_COLOR_FIELD_NAME(): string { return 'cellColor' }
    public static get ROW_COLOR_FIELD_NAME(): string { return 'rowColor' }

    public static get SOURCE_OBJECT_FIELD_NAME(): string { return '__BUSINESS_OBJECT__' }
    public static get SOURCE_COLUMN_MODEL_FIELD_NAME(): string { return '__SOURCE_COLUMN_MODEL__' }

    private static get PAGER_LIGHT_MODE_OPTION_KEY(): string { return 'lightModeEnabled' }
    private static get LOG_PREFIX(): string { return '[SIMAX.Services.Grid]' }
    private get NUMBER_CELL_EDIT_ICON(): string { return 'grid__additional-icon-in-edit-cell-number' }
    private get CELL_EDIT_ICON(): string { return 'grid__additional-icon-in-edit-cell' }

    // #endregion

    // #region Ctor

    constructor(private userSessionService: UserSessionService) { }

    // #endregion

    // #region Data Type Group

    public getColumnModelByCategory(dataTypeGroupCategory: Enums.SimaxDataTypeGroupCategory, moduleName: Enums.SimaxModuleName, options?: GetColumnModelOptions): JQueryPromise<ColumnModel[]>
    public getColumnModelByCategory(dataTypeGroupCategories: Enums.SimaxDataTypeGroupCategory[], moduleName: Enums.SimaxModuleName, options?: GetColumnModelOptions): JQueryPromise<ColumnModel[]>
    public getColumnModelByCategory(dataTypeGroupCategory: Enums.SimaxDataTypeGroupCategory | Enums.SimaxDataTypeGroupCategory[], moduleName: Enums.SimaxModuleName, options: GetColumnModelOptions = {}): JQueryPromise<ColumnModel[]> {

        if (dataTypeGroupCategory == null || moduleName == null) {
            return $.Deferred<ColumnModel[]>().resolve([]).promise()
        }

        const categories: Enums.SimaxDataTypeGroupCategory[] = _.isArray(dataTypeGroupCategory) ? dataTypeGroupCategory : [dataTypeGroupCategory]
        let viewReferenceType: Enums.ReferenceType = null
        let groupReferenceType: Enums.ReferenceType = null
        let groupReferenceValue: number = null
        let prefix: string = null

        if (options != null) {
            viewReferenceType = options.viewReferenceType
            groupReferenceType = options.groupReferenceType
            groupReferenceValue = options.groupReferenceValue
            prefix = options.prefix
        }

        if (options != null && options.skipView) {
            return this.getColumnModels(categories, moduleName, groupReferenceType, groupReferenceValue)
        }

        if (_.isString(prefix)) {
            return this.getColumnModelsByPrefix(categories, moduleName, prefix, groupReferenceType, groupReferenceValue)
        }

        return this.getColumnModelsByView(categories, moduleName, viewReferenceType, groupReferenceType, groupReferenceValue)
    }

    /**
     * For the given referene type returns it's set of grid columns loaded from:
     * 1. WEBSUMAX__MODULE_NAME_POPUP_GRID_PARAMS
     * 2. WEBSIMAX__MODULE_NAME__DEFAULT_GRID_PARAMS if above is empty
     * 3. If above is empty returns a name column. See GridService.getNameColumn().
     */
    public getPopupColumnModelByReferenceType(idReferenceType: Enums.ReferenceType): JQueryPromise<ColumnModel[]> {
        LogHelper.log(GridService.LOG_PREFIX, `Trying to load popup columns for reference type ${idReferenceType}.`)

        const moduleName: Enums.SimaxModuleName = ReferenceTypeComponent.getModuleByReferenceType(idReferenceType)
        return this
            .getColumnModelByCategory(Enums.SimaxDataTypeGroupCategory.WEBSIMAX__MODULE_NAME__POPUP_GRID_PARAMS, moduleName, {
                groupReferenceType: Enums.ReferenceType.IdDistributor,
                groupReferenceValue: this.userSessionService.getLoggedOperator().idDistributor
            })
            .then((popupColumns: ColumnModel[]) => {
                if (_.size(popupColumns) > 0) {
                    LogHelper.log(GridService.LOG_PREFIX, `Loaded ${_.size(popupColumns)} columns from WEBSIMAX__MODULE_NAME__POPUP_GRID_PARAMS.`)

                    return popupColumns
                }

                LogHelper.log(GridService.LOG_PREFIX, `No columns found, trying to load from WEBSIMAX__MODULE_NAME__DEFAULT_GRID_PARAMS.`)

                return this
                    .getColumnModelByCategory(
                        Enums.SimaxDataTypeGroupCategory.WEBSIMAX__MODULE_NAME__DEFAULT_GRID_PARAMS,
                        moduleName,
                        {
                            groupReferenceType: Enums.ReferenceType.IdDistributor,
                            groupReferenceValue: this.userSessionService.getLoggedOperator().idDistributor
                        }
                    )
                    .then((defaultColumns: ColumnModel[]) => {
                        if (_.size(defaultColumns) > 0) {
                            LogHelper.log(GridService.LOG_PREFIX, `Loaded ${_.size(defaultColumns)} columns from WEBSIMAX__MODULE_NAME__DEFAULT_GRID_PARAMS.`)

                            return defaultColumns
                        }

                        LogHelper.log(GridService.LOG_PREFIX, `No columns found, returning the name column.`)

                        return this.userSessionService
                            .getDataProvider().View
                            .getActiveView(idReferenceType)
                            .then((view: OpView) => {
                                const defaultColumn: ColumnModel = this.getNameColumn()
                                const primaryColumn: OpViewColumn = view.getPrimaryKeyColumn()
                                defaultColumn.id = primaryColumn.idDataType
                                return [defaultColumn]
                            })

                    })
            })
            .fail((error: any) => {
                LogHelper.warn(GridService.LOG_PREFIX, 'Unable to load popup columns.', error)

                return []
            })
    }

    // #endregion

    // #region Grid Object

    public getSourceObjectFromGridObject<TObject extends IReferenceType>(gridObject: any, referenceType: Enums.ReferenceType): TObject {
        if (gridObject != null) {
            const sourceObjectJSON: string = gridObject[GridService.SOURCE_OBJECT_FIELD_NAME]

            return ReferenceTypeComponent.createReferenceObject(referenceType, sourceObjectJSON) as TObject
        }

        return null
    }

    /*
     * Generates a grid object from an object implementing the IOpObject interface 
     */
    public gridObjectFromIOpObject(opObject: IOpObject): any {
        if (opObject == null) {
            throw new Error("OpObject cannot be null.")
        }

        const result = {}

        opObject.dynamicValues.forEach((dynamicValue: { Key: string, Value: any }) => {
            result[dynamicValue.Key.toLowerCase()] = dynamicValue.Value
        })

        result[CommonHelper.idField] = opObject.idObject
        result[CommonHelper.nameField] = opObject.toString()

        return result
    }

    public gridObjectFromIReferenceType(referenceType: IReferenceType): any {
        if (referenceType == null) {
            throw new Error("referenceType cannot be null.")
        }

        const result: any = {}

        result[CommonHelper.idField] = referenceType.getReferenceKey()
        result[CommonHelper.nameField] = referenceType.toString()

        return result
    }

    // #endregion

    // #region Columns

    public getColumnModelFromDxColumn(column: DevExpress.ui.dxDataGridColumn): ColumnModel {
        if (column != null) {
            return column[GridService.SOURCE_COLUMN_MODEL_FIELD_NAME]
        }

        return null
    }

    public getToolbarColumn<TObject>(optionsFunction: (object: TObject) => dxToolbarOptions): ColumnModel {
        return this.getWidgetColumn((container: JQuery, cellInfo: any) => {
            const options: dxToolbarOptions = _.isFunction(optionsFunction) ? optionsFunction(cellInfo.data) : null

            if (options != null) {
                const div: JQuery = $('<div></div>')

                div.dxToolbar(options)
                div.appendTo(container)
            }
        })
    }

    public getEditIconInCell(options: any, dataField: string): void {
        if (options != null && dataField != null) {
            if (options.rowType === 'data' && options.column.dataField === dataField) {
                if (options.column.dataType === 'number') {
                    options.cellElement[0].classList.add(this.NUMBER_CELL_EDIT_ICON)
                } else {
                    options.cellElement[0].classList.add(this.CELL_EDIT_ICON)
                }
            }
        }
    }

    /**
     * Returns a ColumnModel that has a Name caption and takes it's value from the toString method of the data source.
     */
    public getNameColumn(): ColumnModel {
        const nameColumn: ColumnModel = new ColumnModel()

        nameColumn.idName = 'name'
        nameColumn.name = 'name'
        nameColumn.type = Enums.DataTypeClass.Text
        nameColumn.caption = LocalizationHelper.getLocalizedText(LocalizationHelper.Text.Name)

        return nameColumn
    }

    public getWidgetColumn(cellTemplateFunc: (container: JQuery, cellInfo: any) => void): ColumnModel {
        return <any>{
            cellTemplate: (container: JQuery, cellInfo: any) => {
                if (_.isFunction(cellTemplateFunc)) {
                    cellTemplateFunc(container, cellInfo)
                }
            },
            name: '',
            referenceTypeId: null,
            sortDesc: false,
            type: 0,
            unitId: null,
            unitName: '',
            where: null,
            width: 70,
            isEditable: false,
            alignment: 'center',
            allowSorting: false
        }
    }

    // #endregion

    // #region Pager

    // https://www.devexpress.com/Support/Center/Question/Details/T583311/dxdatagrid-how-to-enable-light-view-pager

    public isAnyPagerInLightMode(...dxDataGrids: dxDataGrid[]): boolean {
        if (_.size(dxDataGrids) > 0) {
            return dxDataGrids.some((dxDataGrid: dxDataGrid) => this.isPagerInLightMode(dxDataGrid))
        }

        return false
    }

    public isPagerInLightMode(dxDataGrid: dxDataGrid): boolean {
        try {
            const pager: any = this.getGridPager(dxDataGrid)

            if (pager != null) {
                return pager.option(GridService.PAGER_LIGHT_MODE_OPTION_KEY)
            }
        } catch (ex) {
            LogHelper.error(GridService.LOG_PREFIX, ex)
        }

        return null
    }

    public setPagerLigthModeMultiple(useLightMode: boolean, ...dxDataGrids: dxDataGrid[]): void {
        if (_.size(dxDataGrids) > 0) {
            dxDataGrids.forEach((dxDataGrid: dxDataGrid) => this.setPagerLightMode(useLightMode, dxDataGrid))
        }
    }

    public setPagerLightMode(useLightMode: boolean, dxDataGrid: dxDataGrid) {
        try {
            const pager: any = this.getGridPager(dxDataGrid)

            if (pager != null) {
                pager.option(GridService.PAGER_LIGHT_MODE_OPTION_KEY, useLightMode)
            }
        } catch (ex) {
            LogHelper.error(GridService.LOG_PREFIX, ex)
        }
    }

    private getGridPager(dxDataGrid: dxDataGrid): any {
        try {
            if (dxDataGrid != null) {
                const view: any = (<any>dxDataGrid).getView('pagerView')

                if (view != null) {
                    const element: any = view.element()

                    if (element != null) {
                        const pager: any = element.dxPager('instance')

                        if (pager != null) {
                            return pager
                        }
                    }
                }
            }
        } catch (ex) {
            LogHelper.error(GridService.LOG_PREFIX, ex)
        }

        return null
    }

    // #endregion

    // #region Private Methods

    private getColumnModels(dataTypeGroupCategories: Enums.SimaxDataTypeGroupCategory[], moduleName: Enums.SimaxModuleName, groupReferenceType: Enums.ReferenceType, groupReferenceValue: number): JQueryPromise<ColumnModel[]> {
        return this.getColumnModel(dataTypeGroupCategories, moduleName, groupReferenceType, groupReferenceValue, (dataTypeInGroupList: OpDataTypeInGroup[]) => {
            const columnModelList: ColumnModel[] = []
            dataTypeInGroupList
                .forEach((dataTypeInGroup: OpDataTypeInGroup) => {
                    const columnModel: ColumnModel = this.createColumnModelFromDataTypeInGroup(dataTypeInGroup)

                    if (columnModel != null && dataTypeInGroup != null && dataTypeInGroup.dataType != null) {

                        columnModel.name = dataTypeInGroup.dataType.name

                        columnModelList.push(columnModel)
                    }
                })

            return $.Deferred<ColumnModel[]>().resolve(columnModelList).promise()
        })

    }

    private getColumnModelsByView(
        dataTypeGroupCategories: Enums.SimaxDataTypeGroupCategory[],
        moduleName: Enums.SimaxModuleName,
        viewReferenceType: Enums.ReferenceType = null,
        groupReferenceType: Enums.ReferenceType = null,
        groupReferenceValue: number = null
    ): JQueryPromise<ColumnModel[]> {

        let referenceType: Enums.ReferenceType = ReferenceTypeComponent.getReferenceTypeByModule(moduleName)

        if (viewReferenceType != null) {
            referenceType = viewReferenceType
        }

        if (referenceType == null || referenceType == Enums.ReferenceType.None) {
            return $.Deferred<ColumnModel[]>().resolve([]).promise()
        }

        return this.getColumnModel(dataTypeGroupCategories, moduleName, groupReferenceType, groupReferenceValue, (dataTypeInGroupList: OpDataTypeInGroup[]) => {

            return this.userSessionService
                .getDataProvider()
                .View
                .getActiveView(referenceType)
                .then((activeView: OpView) => {
                    if (activeView == null) {
                        return []
                    }

                    const columnModelList: ColumnModel[] = []

                    activeView.columns
                        .forEach((viewColumn: OpViewColumn) => {
                            dataTypeInGroupList.forEach((dataTypeInGroup: OpDataTypeInGroup) => {
                                if (viewColumn.idDataType === dataTypeInGroup.idDataType) {
                                    const columnModel: ColumnModel = this.createColumnModelFromDataTypeInGroup(dataTypeInGroup)

                                    columnModel.name = viewColumn.name.toLowerCase()
                                    columnModel.skipTranslation = viewColumn.shouldSkipTranslation
                                    columnModel.alwaysHidden = viewColumn.isHidden
                                    columnModel.hidden = viewColumn.isHidden
                                    columnModel.showInColumnChooser = !viewColumn.isHidden

                                    columnModelList.push(columnModel)
                                }
                            })
                        })

                    return columnModelList
                })

        })
    }

    private getColumnModelsByPrefix(
        dataTypeGroupCategories: Enums.SimaxDataTypeGroupCategory[],
        moduleName: Enums.SimaxModuleName,
        prefix: string,
        groupReferenceType: Enums.ReferenceType = null,
        groupReferenceValue: number = null
    ): JQueryPromise<ColumnModel[]> {

        return this.getColumnModel(dataTypeGroupCategories, moduleName, groupReferenceType, groupReferenceValue, (dataTypeInGroupList: OpDataTypeInGroup[]) => {

            const columnModelList: ColumnModel[] = dataTypeInGroupList.map((dataTypeInGroup: OpDataTypeInGroup) => {
                const columnModel: ColumnModel = this.createColumnModelFromDataTypeInGroup(dataTypeInGroup)

                columnModel.name = `${prefix}_${dataTypeInGroup.idDataType}_0`

                return columnModel
            })

            return $.Deferred().resolve(columnModelList).promise()
        })
    }

    private createColumnModelFromDataTypeInGroup(dataTypeInGroup: OpDataTypeInGroup): ColumnModel {
        if (dataTypeInGroup == null || dataTypeInGroup.dataType == null) {
            return null
        }

        const columnModel: ColumnModel = new ColumnModel()

        columnModel.id = dataTypeInGroup.idDataType
        columnModel.caption = dataTypeInGroup.toString()
        columnModel.name = TypeHelper.emptyString
        columnModel.type = dataTypeInGroup.dataType.idDataTypeClass
        columnModel.referenceTypeId = dataTypeInGroup.dataType.idReferenceType
        columnModel.isEditable = dataTypeInGroup.dataType.isEditable
        columnModel.format = <any>dataTypeInGroup.dataType.dataTypeFormat
        columnModel.unitId = dataTypeInGroup.dataType.idUnit
        columnModel.unitName = dataTypeInGroup.dataType.unit.toString()
        columnModel.dataType = dataTypeInGroup.dataType
        columnModel['orderNbr'] = dataTypeInGroup.orderNBR
        columnModel.skipTranslation = null

        return columnModel
    }

    private getColumnModel(
        dataTypeGroupCategories: Enums.SimaxDataTypeGroupCategory[],
        moduleName: Enums.SimaxModuleName,
        groupReferenceType: Enums.ReferenceType = null,
        groupReferenceValue: number = null,
        func: (dataTypeInGroupList: OpDataTypeInGroup[]) => JQueryPromise<ColumnModel[]>
    ): JQueryPromise<ColumnModel[]> {
        const promiseList: JQueryPromise<ColumnModel[]>[] = _.map(dataTypeGroupCategories, (dataTypeGroupCategory: Enums.SimaxDataTypeGroupCategory) => {

            return DataTypeGroupComponent
                .getDataTypeInGroupByModule(this.userSessionService.getDataProvider(), dataTypeGroupCategory, moduleName, groupReferenceType, groupReferenceValue)
                .then((dataTypeInGroupList: OpDataTypeInGroup[]) => {
                    if (_.size(dataTypeInGroupList) === 0) {
                        return [] as ColumnModel[]
                    }

                    return func(dataTypeInGroupList)
                        .then((columnModels: ColumnModel[]) => {
                            return this.sortByOrderNbr(columnModels)
                        })
                })
        })

        return $.when(...promiseList)
    }

    private sortByOrderNbr(columnModelList: ColumnModel[]): ColumnModel[] {
        if (_.isArray(columnModelList)) {
            return columnModelList.sort((columnModel1: ColumnModel, columnModel2: ColumnModel) => columnModel1['orderNbr'] - columnModel2['orderNbr'])
        }

        return columnModelList
    }

    // #endregion
}

export class GridServiceProvider implements ng.IServiceProvider {

    // #region Members


    // #endregion


    // #region IServiceProvider Implementation 

    public $get = [USER_SESSION_SERVICE_NAME, function (userSessionService: UserSessionService) {
        return new GridService(userSessionService)
    }]

    // #endregion
}